import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { securePut } from "../../config/api";
import { Helmet } from "react-helmet-async";
import { SaveCancelTop } from "../../components/PrimaryButton";
import Input from "../../components/Input";
import ClientMap from "./ClientMap";
import SnackbarNotification from '../../components/SnackbarNotification';
import LoadingLogo from "../../components/LoadingLogo";
import Breadcrumbs from "../../components/Breadcrumbs";
import checkIcon from "../../icons/checkGr.svg";
import { parseFrenchPieceStringToKeys, joinFrenchKeys } from "../../utils/paymentModesFr";

const getDynamicFields = (fields) => {
  return fields.reduce((acc, field) => {
    acc[field.name] = field.value || "";
    return acc;
  }, {});
};

const getCityId = (cityName, baseFields) => {
  const cityOption = baseFields[2]?.options.find((city) => city.label === cityName);
  return cityOption ? cityOption.value : 0;
};

const prepareRequestBody = (formValues, paymentMethods, initialValues, additionalFields) => {
  const fields = additionalFields.reduce((acc, field) => {
    acc[field.name] = formValues[field.name];
    return acc;
  }, {});

  return {
    ...(initialValues?.id && { id: initialValues.id }),
    fields,
    cityId: Number(formValues.cityId) || 0,
    latitude: parseFloat(formValues.latitude),
    longitude: parseFloat(formValues.longitude),
    address: formValues.address,
    name: formValues.name,
    code: formValues.code,
    taxId: formValues.taxId,
    contactPrincipal: formValues.contactPrincipal,
    phone: formValues.phone,
    encoursMaximum: Number(formValues.encoursMaximum) || 0,
    echeanceAutorisee: Number(formValues.echeanceAutorisee) || 0,
    pieceReglementAutorisee: joinFrenchKeys(paymentMethods),
  };
};

const ClientForm = ({
  title,
  initialValues = {},
  baseFields = [],
  additionalFields = [],
  paymentMode,
  onSuccess,
}) => {
  const [formValues, setFormValues] = useState({});
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const history = useHistory();
  const { t } = useTranslation();
  const location = useLocation();
  const [loading, setLoading] = useState(true);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const PARAMETRAGE = [
    {
      label: t("fields.clients.encoursMaximum"),
      name: "encoursMaximum",
      type: "text",
      required: true,
    },
    {
      label: t("fields.clients.echeanceAutorisee"),
      name: "echeanceAutorisee",
      type: "text",
      required: true,
    },
  ];

  useEffect(() => {
    const checkDataReadiness = () => {
      const baseFieldsReady = baseFields.every(field =>
        !field.options || field.options.length > 0
      );

      if (initialValues.id) {
        return formValues.id !== "" && baseFieldsReady;
      }
      return baseFieldsReady;
    };

    if (checkDataReadiness()) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [formValues, baseFields, initialValues.id]);

  useEffect(() => {
    if (initialValues) {
      const dynamicFields = getDynamicFields(initialValues.fields || []);
      const resolvedCityId = getCityId(initialValues.cityName, baseFields);

      setFormValues({
        ...initialValues,
        id: initialValues.id || "",
        name: initialValues.name || "",
        code: initialValues.code || "",
        cityId: resolvedCityId,
        address: initialValues.address || "",
        taxId: initialValues.taxId || "",
        latitude: initialValues.latitude || "",
        longitude: initialValues.longitude || "",
        contactPrincipal: initialValues.contactPrincipal || "",
        phone: initialValues.phone || "",
        encoursMaximum: initialValues.encoursMaximum || "",
        echeanceAutorisee: initialValues.echeanceAutorisee || "",
        ...dynamicFields,
      });

      const pieceLabel = initialValues.pieceReglementAutorisee || "";
      setPaymentMethods(parseFrenchPieceStringToKeys(pieceLabel));
    }
  }, [initialValues, baseFields]);

  const handleInputChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const togglePaymentMethod = (frKey) => {
    setPaymentMethods((prev) =>
      prev.includes(frKey)
        ? prev.filter((m) => m !== frKey)
        : [...prev, frKey]
    );
  };

  const handleMapPositionChange = (position) => {
    setFormValues((prev) => ({
      ...prev,
      latitude: position.lat,
      longitude: position.lng,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const requiredFields = [...baseFields, ...PARAMETRAGE, ...additionalFields, ...paymentMode];
    const missing = requiredFields.filter(f => f.required && !formValues[f.name]);
    if (missing.length > 0) {
      showSnackbar(`Veuillez remplir les champs obligatoires : ${missing.map(f => f.label).join(', ')}`, 'error');
      return;
    }
    const requestBody = prepareRequestBody(formValues, paymentMethods, initialValues, additionalFields);
    try {
      await securePut("/clients/add", requestBody, showSnackbar);
      onSuccess();

      history.push({
        pathname: "/clients",
        state: {
          filters: location.state?.filters || {},
          page: location.state?.page || 0,
          size: location.state?.size || 100,
          showSnackbar: true,
          snackbarMessage: "Client added successfully",
          snackbarSeverity: "success",
        },
      });
    } catch (error) {
      console.error("Error adding client:", error);
    }
  };

  const handleCancel = () => {
    if (location.pathname.includes("details")) {
      history.push({
        pathname: `/clients/details/${formValues.id}`,
        state: {
          filters: location.state?.filters || {},
          page: location.state?.page || 0,
          size: location.state?.size || 100,
        },
      });
    } else {
      history.push({
        pathname: "/clients",
        state: {
          filters: location.state?.filters || {},
          page: location.state?.page || 0,
          size: location.state?.size || 100,
        },
      });
    }
  };


  return (
    <div className="space-y-6">
      <Helmet>
        <title>GrayFox - {t("titles.clients")}</title>
      </Helmet>
      <div className="flex flex-col space-y-2">
        <Breadcrumbs />
        <div className="flex justify-between items-center">
          <h2 className="text-[32px] font-bold text-black">{title}</h2>
          <SaveCancelTop onCancel={handleCancel} onSave={handleSubmit} />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-[300px]">
          <LoadingLogo />
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="grid grid-cols-3 gap-6">
          <div className="col-span-2 space-y-6 overflow-y-auto pr-4">
            {/* Base Fields */}
            {baseFields.length > 0 && (
              <div className="flex-1 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {baseFields.map((field, index) => (
                    <Input
                      key={index}
                      label={field.label}
                      name={field.name}
                      type={field.type || "text"}
                      value={formValues[field.name] || ""}
                      onChange={(value) => handleInputChange(field.name, value)}
                      options={field.options}
                      required={field.required}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Additional Fields */}
            {additionalFields.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-[16px] font-medium text-[#ACAFC6]">{t("fields_types.additional")}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {additionalFields
                    .map((field, index) => (
                      <Input
                        key={index}
                        label={field.label}
                        name={field.name}
                        type={field.type || "text"}
                        value={formValues[field.name] || ""}
                        onChange={(value) => handleInputChange(field.name, value)}
                        options={field.options || []}
                        required={field.required}
                      />
                    ))}
                </div>
              </div>
            )}

            {/* Paramétrage Section */}
            <div className="space-y-4">
              <h3 className="text-[16px] font-medium text-[#ACAFC6]">
                {t("fields_types.parameters")}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {PARAMETRAGE.map((field, index) => (
                  <Input
                    key={index}
                    label={field.label}
                    name={field.name}
                    type={field.type}
                    value={formValues[field.name] || ""}
                    required={field.required}
                    onChange={(value) => handleInputChange(field.name, value)}
                  />
                ))}
              </div>
            </div>

            {/* Payment Methods */}
            <div className="space-y-4">
              <h3 className="text-[16px] font-medium text-[#ACAFC6]">{t("fields_types.pièce")}</h3>
              <div className="flex flex-wrap gap-4">
                {(paymentMode || []).map((method, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`flex items-center px-4 py-2 rounded-[12px] border ${paymentMethods.includes(method.value)
                      ? "bg-white text-[#06D6A0] border-[#06D6A0] font-bold"
                      : "bg-[#FFFFFF] text-[#CBCBCD] border-[#CBCBCD] font-medium"
                      } hover:border-[#06D6A0] transition`}
                    onClick={() => togglePaymentMethod(method.value)}
                  >
                    {paymentMethods.includes(method.value) && (
                      <img src={checkIcon} alt="Check Icon" className="w-5 h-5 mr-2 opacity-100" />
                    )}
                    {method.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Client Location Map */}
          <div className="col-span-1 space-y-4">
            <h3 className="text-[16px] font-medium text-[#ACAFC6]">{t("fields_types.clientLocation")}</h3>
            <ClientMap
              latitude={formValues.latitude}
              longitude={formValues.longitude}
              onPositionChange={handleMapPositionChange}
            />
          </div>
        </form>
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );
};

export default ClientForm;
